import React from 'react';
import { OwnershipRecord, PendingChange } from '../types/ownership';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import { Edit2, Trash2, AlertCircle, RotateCcw } from 'lucide-react';
interface OwnershipTableProps {
  records: OwnershipRecord[];
  onEdit: (record: OwnershipRecord) => void;
  onDelete: (id: string) => void;
  isHistoricalView: boolean;
  pendingChanges?: PendingChange[];
}
export function OwnershipTable({
  records,
  onEdit,
  onDelete,
  isHistoricalView,
  pendingChanges = []
}: OwnershipTableProps) {
  const today = new Date().toISOString().split('T')[0];
  if (records.length === 0) {
    return <div className="text-center py-12 bg-[#f3f2f2] rounded border border-dashed border-[#dddbda]" data-id="element-114">
        <p className="text-[#706e6b]" data-id="element-115">No ownership records found.</p>
      </div>;
  }
  return <div className="overflow-x-auto border border-[#dddbda] rounded-[4px]" data-id="element-116">
      <table className="w-full text-sm text-left" data-id="element-117">
        <thead className="bg-[#f3f2f2] text-[#3e3e3c] font-bold uppercase text-xs" data-id="element-118">
          <tr data-id="element-119">
            <th className="px-4 py-2 border-b border-[#dddbda]" data-id="element-120">Member</th>
            <th className="px-4 py-2 border-b border-[#dddbda]" data-id="element-121">Ownership %</th>
            <th className="px-4 py-2 border-b border-[#dddbda]" data-id="element-122">Start Date</th>
            <th className="px-4 py-2 border-b border-[#dddbda]" data-id="element-123">End Date</th>
            <th className="px-4 py-2 border-b border-[#dddbda]" data-id="element-124">Status</th>
            <th className="px-4 py-2 border-b border-[#dddbda] text-right" data-id="element-125">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-[#dddbda]" data-id="element-126">
          {records.map(record => {
          // Determine pending state
          const pendingChange = pendingChanges.find(c => c.record.id === record.id);
          const isPendingAdd = pendingChange?.type === 'add';
          const isPendingEdit = pendingChange?.type === 'edit';
          const isPendingDelete = pendingChange?.type === 'delete';
          const isActive = record.effectiveStartDate <= today && (!record.effectiveEndDate || record.effectiveEndDate >= today);
          const isFuture = record.effectiveStartDate > today;
          const isHistorical = !isActive && !isFuture;
          // Row styling based on state
          let rowClass = 'hover:bg-[#f3f2f2] transition-colors ';
          if (isPendingAdd) rowClass += 'bg-green-50 border-l-4 border-l-green-500 ';else if (isPendingEdit) rowClass += 'bg-amber-50 border-l-4 border-l-amber-500 ';else if (isPendingDelete) rowClass += 'bg-red-50 border-l-4 border-l-red-500 opacity-60 ';else if (isHistorical) rowClass += 'bg-gray-50 text-gray-500 ';else rowClass += 'border-l-4 border-l-transparent '; // maintain spacing
          return <tr key={record.id} className={rowClass} data-id="element-127">
                <td className="px-4 py-3 font-medium text-[#080707]" data-id="element-128">
                  <div className={isPendingDelete ? 'line-through' : ''} data-id="element-129">
                    {record.memberName}
                    {record.isPrimaryMember && <span className="ml-2 inline-flex" data-id="element-130">
                        <Badge variant="brand" data-id="element-131">Primary</Badge>
                      </span>}
                  </div>
                </td>
                <td className="px-4 py-3" data-id="element-132">
                  <span className={isPendingDelete ? 'line-through' : ''} data-id="element-133">
                    {record.ownershipPercentage}%
                  </span>
                </td>
                <td className="px-4 py-3" data-id="element-134">
                  <span className={isPendingDelete ? 'line-through' : ''} data-id="element-135">
                    {new Date(record.effectiveStartDate).toLocaleDateString()}
                  </span>
                </td>
                <td className="px-4 py-3" data-id="element-136">
                  <span className={isPendingDelete ? 'line-through' : ''} data-id="element-137">
                    {record.effectiveEndDate ? new Date(record.effectiveEndDate).toLocaleDateString() : '-'}
                  </span>
                </td>
                <td className="px-4 py-3" data-id="element-138">
                  {isPendingAdd ? <Badge className="bg-green-100 text-green-800 border border-green-200" data-id="element-139">
                      New
                    </Badge> : isPendingEdit ? <Badge className="bg-amber-100 text-amber-800 border border-amber-200" data-id="element-140">
                      Modified
                    </Badge> : isPendingDelete ? <Badge className="bg-red-100 text-red-800 border border-red-200" data-id="element-141">
                      Pending Delete
                    </Badge> : isFuture ? <Badge variant="warning" data-id="element-142">Future</Badge> : isActive ? <Badge variant="success" data-id="element-143">Active</Badge> : <Badge variant="neutral" data-id="element-144">Historical</Badge>}
                </td>
                <td className="px-4 py-3 text-right" data-id="element-145">
                  <div className="flex items-center justify-end gap-2" data-id="element-146">
                    {isPendingDelete ? <button onClick={() => onDelete(record.id)} // Re-clicking delete on a pending delete could undo it? For now, just disable
                className="p-1 text-gray-400 cursor-not-allowed" disabled title="Pending Deletion" data-id="element-147">
                        <Trash2 className="w-4 h-4" data-id="element-148" />
                      </button> : <>
                        <button onClick={() => onEdit(record)} className="p-1 text-[#0176d3] hover:bg-[#eef4ff] rounded" title="Edit" data-id="element-149">
                          <Edit2 className="w-4 h-4" data-id="element-150" />
                        </button>
                        {isFuture || isPendingAdd ? <button onClick={() => onDelete(record.id)} className="p-1 text-[#ba0517] hover:bg-[#fff1f0] rounded" title="Delete" data-id="element-151">
                            <Trash2 className="w-4 h-4" data-id="element-152" />
                          </button> : <button className="p-1 text-gray-300 cursor-not-allowed" title="Cannot delete active/historical records" disabled data-id="element-153">
                            <Trash2 className="w-4 h-4" data-id="element-154" />
                          </button>}
                      </>}
                  </div>
                </td>
              </tr>;
        })}
        </tbody>
      </table>
    </div>;
}